import React, { useState, useEffect } from 'react';
import { Pagination, Spin } from 'antd';
import { 
  RightOutlined, 
  DownOutlined, 
  CalendarOutlined,
  FileTextOutlined,
  BarChartOutlined,
  RiseOutlined
} from '@ant-design/icons';
import './FinanceViewNew.css';

// Use the existing ForecastRevenueResponse type
interface ForecastRevenueResponse {
  revenueForecast?: {
    clients?: Client[];
    TotalRevenue?: {
      projects: number;
      pipeline: number;
      total: number;
      actuals: number;
      quarters: QuarterData[];
    };
  };
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
}

interface QuarterData {
  quarter: string;
  status?: string;
  projectRevenue: number;
  pipelineRevenue: number;
  total: number;
  actualRevenue: number;
}

interface Department {
  type: string;
  name: string | null;
  totals: {
    projectRevenue: number;
    pipelineRevenue: number;
    total: number;
    actualRevenue: number;
  };
  quarters: QuarterData[];
}

interface BusinessUnit {
  type: string;
  name: string;
  totals: {
    projectRevenue: number;
    pipelineRevenue: number;
    total: number;
    actualRevenue: number;
  };
  quarters: QuarterData[];
  departments: Department[];
}

interface Client {
  type: string;
  name: string;
  totals: {
    projectRevenue: number;
    pipelineRevenue: number;
    total: number;
    actualRevenue: number;
  };
  quarters: QuarterData[];
  businessUnits: BusinessUnit[];
}

interface SalesViewNewProps {
  data?: ForecastRevenueResponse | null;
  loading?: boolean;
  onOpenFilters?: () => void;
  onPageChange?: (page: number, pageSize: number) => void;
  currentPage?: number;
  pageSize?: number;
  totalElements?: number;
}

const SalesViewNew: React.FC<SalesViewNewProps> = ({ 
  data, 
  loading = false, 
  onOpenFilters,
  onPageChange,
  currentPage = 1,
  pageSize = 10,
  totalElements = 0
}) => {
  const [selectedQuarter, setSelectedQuarter] = useState<string>('All');
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedBUs, setExpandedBUs] = useState<Set<string>>(new Set());
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [expandedQuarters, setExpandedQuarters] = useState<Set<string>>(new Set());

  // Format currency
  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toFixed(2)}`;
  };

  // Toggle expand/collapse
  const toggleClient = (clientName: string) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientName)) {
      newExpanded.delete(clientName);
    } else {
      newExpanded.add(clientName);
    }
    setExpandedClients(newExpanded);
  };

  const toggleBU = (buKey: string) => {
    const newExpanded = new Set(expandedBUs);
    if (newExpanded.has(buKey)) {
      newExpanded.delete(buKey);
    } else {
      newExpanded.add(buKey);
    }
    setExpandedBUs(newExpanded);
  };

  const toggleDept = (deptKey: string) => {
    const newExpanded = new Set(expandedDepts);
    if (newExpanded.has(deptKey)) {
      newExpanded.delete(deptKey);
    } else {
      newExpanded.add(deptKey);
    }
    setExpandedDepts(newExpanded);
  };

  const toggleQuarters = (key: string) => {
    const newExpanded = new Set(expandedQuarters);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedQuarters(newExpanded);
  };

  // Get all unique quarters
  const getAllQuarters = (): string[] => {
    if (!data?.revenueForecast?.TotalRevenue?.quarters) return [];
    return data.revenueForecast.TotalRevenue.quarters.map(q => q.quarter);
  };

  // Get filtered data based on selected quarter
  const getQuarterData = (quarters: QuarterData[], quarterName: string) => {
    if (quarterName === 'All') {
      return {
        projectRevenue: quarters.reduce((sum, q) => sum + q.projectRevenue, 0),
        pipelineRevenue: quarters.reduce((sum, q) => sum + q.pipelineRevenue, 0),
        total: quarters.reduce((sum, q) => sum + q.total, 0),
        actualRevenue: quarters.reduce((sum, q) => sum + q.actualRevenue, 0)
      };
    }
    const quarter = quarters.find(q => q.quarter === quarterName);
    return quarter || { projectRevenue: 0, pipelineRevenue: 0, total: 0, actualRevenue: 0 };
  };

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    if (!data?.revenueForecast?.TotalRevenue) {
      return {
        totalForecast: 0,
        actualRevenue: 0,
        pipelineRevenue: 0,
        projectsRevenue: 0,
        realization: 0
      };
    }

    const total = data.revenueForecast.TotalRevenue;
    
    if (selectedQuarter === 'All') {
      return {
        totalForecast: total.total,
        actualRevenue: total.actuals,
        pipelineRevenue: total.pipeline,
        projectsRevenue: total.projects,
        realization: total.total > 0 ? (total.actuals / total.total) * 100 : 0
      };
    }
    
    const quarterData = getQuarterData(total.quarters, selectedQuarter);

    return {
      totalForecast: quarterData.total || (quarterData.projectRevenue + quarterData.pipelineRevenue),
      actualRevenue: quarterData.actualRevenue,
      pipelineRevenue: quarterData.pipelineRevenue,
      projectsRevenue: quarterData.projectRevenue,
      realization: quarterData.total > 0 ? (quarterData.actualRevenue / quarterData.total) * 100 : 0
    };
  };

  const metrics = getSummaryMetrics();
  const quarters = getAllQuarters();

  // Pagination handler
  const handlePageChange = (page: number, size?: number) => {
    if (onPageChange) {
      onPageChange(page, size || pageSize);
    }
  };

  const allClients = data?.revenueForecast?.clients || [];
  const totalRevenue = data?.revenueForecast?.TotalRevenue;

  // Render Quarter Rows
  const renderQuarterRows = (quarters: QuarterData[], parentKey: string, indentClass: string) => {
    return quarters.map(quarter => (
      <tr key={`quarter-${parentKey}-${quarter.quarter}`} className={`quarter-row ${indentClass}`}>
        <td className="name-cell">
          <CalendarOutlined />
          <span className="quarter-text">{quarter.quarter}</span>
          {quarter.status === 'Current' && <span className="current-badge-inline">Current</span>}
        </td>
        <td className="amount-cell">{formatCurrency(quarter.projectRevenue)}</td>
        <td className="amount-cell">{formatCurrency(quarter.pipelineRevenue)}</td>
        <td className="amount-cell total-cell">{formatCurrency(quarter.total)}</td>
        <td className="amount-cell actuals-cell">{formatCurrency(quarter.actualRevenue)}</td>
      </tr>
    ));
  };

  // Render Department Row
  const renderDeptRow = (dept: Department, clientName: string, buName: string) => {
    const deptKey = `${clientName}-${buName}-${dept.name}`;
    const quartersExpanded = expandedQuarters.has(deptKey);
    const quarterCount = dept.quarters.length;

    return (
      <>
        <tr key={deptKey} className="dept-row-sales">
          <td className="name-cell">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              <span className="name-text">{dept.name || 'Unknown Department'}</span>
              <span 
                className="quarter-badge clickable"
                onClick={() => toggleQuarters(deptKey)}
                style={{ cursor: 'pointer' }}
              >
                <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
              </span>
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(dept.totals.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(dept.totals.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(dept.totals.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(dept.totals.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(dept.quarters, deptKey, 'sales-dept-quarter-indent')}
      </>
    );
  };

  // Render Business Unit Row
  const renderBURow = (bu: BusinessUnit, clientName: string) => {
    const buKey = `${clientName}-${bu.name}`;
    const isExpanded = expandedBUs.has(buKey);
    const quartersExpanded = expandedQuarters.has(buKey);
    const quarterCount = bu.quarters.length;

    return (
      <>
        <tr key={buKey} className="bu-row-sales">
          <td className="name-cell">
            <span onClick={() => toggleBU(buKey)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
              <span className="name-text">{bu.name}</span>
              <span 
                className="quarter-badge clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleQuarters(buKey);
                }}
                style={{ cursor: 'pointer' }}
              >
                <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
              </span>
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(bu.totals.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(bu.totals.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(bu.totals.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(bu.totals.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(bu.quarters, buKey, 'sales-bu-quarter-indent')}
        {isExpanded && bu.departments?.map((dept, index) => (
          <React.Fragment key={`${buKey}-dept-${index}`}>
            {renderDeptRow(dept, clientName, bu.name)}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Render Client Row
  const renderClientRow = (client: Client) => {
    const isExpanded = expandedClients.has(client.name);
    const quartersExpanded = expandedQuarters.has(`client-${client.name}`);
    const quarterCount = client.quarters.length;

    return (
      <>
        <tr key={client.name} className="client-row-sales">
          <td className="name-cell">
            <span onClick={() => toggleClient(client.name)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
              <span className="name-text">{client.name}</span>
              <span 
                className="quarter-badge clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleQuarters(`client-${client.name}`);
                }}
                style={{ cursor: 'pointer' }}
              >
                <CalendarOutlined /> {quartersExpanded ? 'Hide Qtrs' : `${quarterCount} Qtrs`}
              </span>
            </span>
          </td>
          <td className="amount-cell">{formatCurrency(client.totals.projectRevenue)}</td>
          <td className="amount-cell">{formatCurrency(client.totals.pipelineRevenue)}</td>
          <td className="amount-cell total-cell">{formatCurrency(client.totals.total)}</td>
          <td className="amount-cell actuals-cell">{formatCurrency(client.totals.actualRevenue)}</td>
        </tr>
        {quartersExpanded && renderQuarterRows(client.quarters, `client-${client.name}`, 'sales-client-quarter-indent')}
        {isExpanded && client.businessUnits?.map((bu, index) => (
          <React.Fragment key={`${client.name}-bu-${index}`}>
            {renderBURow(bu, client.name)}
          </React.Fragment>
        ))}
      </>
    );
  };

  if (!data) {
    return (
      <div className="no-data-container">
        <p>No revenue forecast data available.</p>
      </div>
    );
  }

  return (
    <div className="finance-view-new">
      {/* Quarter Tabs */}
      <div className="quarter-section">
        <div className="quarter-tabs">
          <button 
            className={`quarter-tab ${selectedQuarter === 'All' ? 'active' : ''}`}
            onClick={() => setSelectedQuarter('All')}
          >
            All
          </button>
          {quarters.map(quarter => {
            const isCurrentQuarter = data?.revenueForecast?.TotalRevenue?.quarters
              .find(q => q.quarter === quarter)?.status === 'Current';
            
            return (
              <button
                key={quarter}
                className={`quarter-tab ${
                  selectedQuarter === quarter ? 'active' : ''
                } ${isCurrentQuarter ? 'current' : ''}`}
                onClick={() => setSelectedQuarter(quarter)}
              >
                {quarter}
                {isCurrentQuarter && <span className="current-badge">Current</span>}
              </button>
            );
          })}
        </div>

        <div className="quarter-summary">
          <div className="summary-item">
            <span className="summary-label">Forecast:</span>
            <span className="summary-value">{formatCurrency(metrics.totalForecast)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Actuals:</span>
            <span className="summary-value green">{formatCurrency(metrics.actualRevenue)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Realization:</span>
            <span className="summary-value">{metrics.realization.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Revenue Forecast Table */}
      <div className="revenue-forecast-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0 }}>Revenue Forecast</h3>
            <span style={{ color: '#6b7280', fontSize: '14px', fontWeight: 500 }}>
              <FileTextOutlined /> {totalElements || 0} Clients
            </span>
          </div>
          {onOpenFilters && (
            <button
              onClick={onOpenFilters}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                height: '44px',
                paddingLeft: '24px',
                paddingRight: '24px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                fontWeight: 500,
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 1024 1024" fill="currentColor">
                <path d="M880.1 154H143.9c-24.5 0-39.8 26.7-27.5 48L349 597.4V838c0 17.7 14.2 32 31.8 32h262.4c17.6 0 31.8-14.3 31.8-32V597.4L907.7 202c12.2-21.3-3.1-48-27.6-48zM603.4 798H420.6V642h182.9v156zm9.6-236.6l-9.5 16.6h-183l-9.5-16.6L212.7 226h598.6L613 561.4z"/>
              </svg>
              Open Filters
            </button>
          )}
        </div>

        <div className="table-container" style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <Spin size="large" tip="Loading data..." />
            </div>
          )}
          <table className="revenue-table">
            <thead>
              <tr>
                <th className="name-header">CLIENT / BU / DEPT</th>
                <th className="amount-header">PROJECTS</th>
                <th className="amount-header">PIPELINE</th>
                <th className="amount-header">TOTAL</th>
                <th className="amount-header">ACTUALS</th>
              </tr>
            </thead>
            <tbody>
              {allClients.map(client => renderClientRow(client))}
            </tbody>
            <tfoot>
              {/* Total Row */}
              <tr className="total-row">
                <td className="name-cell">
                  <strong>Total Revenue</strong>
                  <span 
                    className="quarter-badge clickable"
                    onClick={() => toggleQuarters('total-revenue')}
                    style={{ cursor: 'pointer' }}
                  >
                    <CalendarOutlined /> {expandedQuarters.has('total-revenue') ? 'Hide Qtrs' : `${quarters.length} Qtrs`}
                  </span>
                </td>
                <td className="amount-cell"><strong>{formatCurrency(metrics.projectsRevenue)}</strong></td>
                <td className="amount-cell"><strong>{formatCurrency(metrics.pipelineRevenue)}</strong></td>
                <td className="amount-cell total-cell"><strong>{formatCurrency(metrics.totalForecast)}</strong></td>
                <td className="amount-cell actuals-cell"><strong>{formatCurrency(metrics.actualRevenue)}</strong></td>
              </tr>
              {expandedQuarters.has('total-revenue') && totalRevenue?.quarters && 
                renderQuarterRows(totalRevenue.quarters, 'total', 'total-quarter-indent')
              }
            </tfoot>
          </table>
        </div>
        
        {/* Pagination */}
        {totalElements > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', marginBottom: '16px', background: 'white', padding: '16px', borderRadius: '8px' }}>
            <Pagination
              current={currentPage}
              total={totalElements}
              pageSize={pageSize}
              onChange={handlePageChange}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesViewNew;
